<template>
    <or-modal
        v-if="knowledgeBases"
        ref="new-model-modal"
        :dismissible="!isLoading"
        :title="modalTitle"
        @close="closeModal"
    >
        <form
            class="wrap"
            @submit.prevent="onSubmit"
            @keydown.enter.prevent = ""
        >
            <div
                v-if="isImportMode"
                class="import-wrap"
            >
                <div class="import-wrap__wrapper">
                    <div :class="['drag-zone', {'drag-zone_dragged': dragging }]">
                        <div class="drag-title">
                            <or-icon
                              class="cloud"
                              icon="backup"
                            />
                            <br>
                          <i18n path="common.modals.create_modal.dragZone.description" tag="p">
                            <span class='bold' place="selectFromComputer">
                              {{ $t('common.modals.create_modal.dragZone.uploadFromComputer') }}
                            </span>
                          </i18n>
                          <span>
                            {{ $t('common.modals.create_modal.dragZone.supportingFormats') }}
                          </span>
                        </div>
                        <input
                            class="drag-input"
                            type="file"
                            ref="upload"
                            :accept="`${supportedFileTypes.JSON}, ${supportedFileTypes.CSV}`"
                            @change="onFileSelection"
                            @dragenter="dragging=true"
                            @dragleave="dragging=false"
                            @drop="dragging=false"
                        >
                    </div>
                </div>
                <div slot="footer" class="btn-group">
                    <or-button
                        color="primary"
                        type="secondary"
                        @click="isImportMode = false"
                    >
                      {{ $t('generic.cancel') }}
                    </or-button>
                </div>
            </div>

            <div v-else>
                <div class="wrap__img-row">
                    <div
                        class="img-wrap"
                        :style="knowledgeModelImageStyles"
                    />
                    <or-textbox
                        v-model="$v.newKnowledgeModel.imageSrc.$model"
                        class="img-input"
                        :placeholder="$t('common.modals.create_modal.imageSrc.placeholder')"
                        :disabled="isLoading"
                        :invalid="$v.newKnowledgeModel.imageSrc.$dirty && $v.newKnowledgeModel.imageSrc.$invalid"
                        :error="errors.imageSrc"
                    />
                </div>
                <or-textbox
                    v-model="$v.newKnowledgeModel.name.$model"
                    enforceMaxlength
                    ref="kbTitle"
                    :label="$t('generic.title')"
                    class="kb-title"
                    :maxlength="22"
                    :disabled="isLoading"
                    :invalid="$v.newKnowledgeModel.name.$dirty && $v.newKnowledgeModel.name.$invalid"
                    :error="errors.name"
                    showErrorText="persistently"
                />
                <or-textbox
                    v-model="newKnowledgeModel.desc"
                    type="text"
                    name="description"
                    :label="$t('generic.description')"
                    enforceMaxlength
                    :maxlength="128"
                    :multi-line="true"
                    :rows="3"
                    :disabled="isLoading"
                />
                <div class="entity-wrap">
                    <or-switch
                        v-model="hasEntityExtraction"
                        :disabled="isLoading"
                    >
                      {{ $t('common.modals.create_modal.enableEntitiesExtraction') }}
                    </or-switch>
                </div>
                <or-select
                    v-if="hasEntityExtraction"
                    v-model="newKnowledgeModel.culture"
                    has-search
                    name="lang"
                    class="lang-select"
                    :label="$t('common.modals.create_modal.language')"
                    :options="modelLanguages"
                    :disabled="isLoading"
                >
                    <template
                        slot-scope="props"
                        slot="option"
                        class="lang-option"
                    >
                        <span>{{props.option.label}}</span>
                        <img :src="props.option.flag">
                    </template>
                </or-select>
                <or-alert
                    v-if="modelsWithEntityExtranctionOverflow"
                    type="warning"
                    :dismissible="false"
                >
                  {{ warningLabel }}
                </or-alert>
                <div class="wrap__btn-row">
                    <or-button
                        v-if="!files.length"
                        type="secondary"
                        color="primary"
                        icon="publish"
                        disableRipple
                        buttonType="button"
                        @click="isImportMode = true"
                    >
                      {{ $t('common.modals.create_modal.import') }}
                    </or-button>
                    <div v-else class="file-wrap">
                        <span class="file-label">{{files[0].name}}</span>
                        <or-icon-button
                            class="delete-btn flat"
                            icon="delete"
                            type="secondary"
                            size="small"
                            :disabled="isLoading"
                            @click.native="deleteFile(files[0])"
                        />
                    </div>
                    <or-button
                        color="primary"
                        buttonType="submit"
                        :loading="isLoading"
                        :disabled="modelsWithEntityExtranctionOverflow"
                    >
                      {{ $t('common.modals.create_modal.create') }}
                    </or-button>
                </div>
                <div
                    v-if="creationError"
                    class="creation-error"
                >
                  {{ $t('common.modals.create_modal.creationError', { creationError }) }}
                </div>
            </div>
        </form>
    </or-modal>
</template>

<script>
import KnowledgeBus from '@/KnowledgeBus'
import { mapActions, mapState } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { isValidURL } from '@/utils/validations'
import { languages } from '@/modules/luis-languages'
import { flags } from '@/modules/language-flags-mapping'
import get from 'lodash/get'

export default {
  mixins: [validationMixin],

  created () {
    KnowledgeBus.$on('create::open', this.openModal)
  },

  beforeDestroy () {
    KnowledgeBus.$off('create::open', this.openModal)
  },

  data () {
    return {
      newKnowledgeModel: {
        name: '',
        desc: '',
        imageSrc: '',
        culture: 'en-us'
      },
      supportedFileTypes: {
        CSV: 'text/csv',
        JSON: 'application/json'
      },
      isImportMode: false,
      dragging: false,
      files: [],
      hasEntityExtraction: true,
      creationError: null,
      isLoading: false,
      defaultImage: 'https://qa-filesanddata-asset-manager-xux8mqnqka.s3-us-west-2.amazonaws.com/6ceadd6c-dbe3-4cb6-8643-889e8d339c77/nlu-ui/kb-default.png',
      extractionKmNumber: 10,
      warningLabel: ''
    }
  },

  computed: {
    ...mapState('knowledgebase', [
      'knowledgeBases',
      'limits'
    ]),
    ...mapState('auth', ['userId']),

    modelsWithEntityExtraction () {
      return _.filter(this.knowledgeBases, model => !_.isEmpty(model.extensions))
    },

    modelsWithEntityExtranctionOverflow () {
      return this.hasEntityExtraction &&
        this.modelsWithEntityExtraction.length >= this.extractionKmNumber
    },

    errors () {
      const errors = {}
      const { newKnowledgeModel } = this.$v

      if (newKnowledgeModel.name.$invalid) {
        if (!newKnowledgeModel.name.unique) {
          errors.name = this.$t('common.modals.create_modal.errors.name.unique')
        }
        if (!newKnowledgeModel.name.required) {
          errors.name = this.$t('common.modals.create_modal.errors.name.required')
        }
      }

      if (newKnowledgeModel.imageSrc.$invalid) {
        if (!newKnowledgeModel.imageSrc.validUrl) {
          errors.imageSrc = this.$t('common.modals.create_modal.errors.imageSrc.invalidUrl')
        }
      }

      return errors
    },

    modalTitle () {
      return this.isImportMode
        ? this.$t('common.modals.create_modal.importKnowledgeModel')
        : this.$t('common.modals.create_modal.createKnowledgeModel')
    },

    modelLanguages () {
      return _.map(languages, item => {
        const flag = _.find(flags, ['value', _.toLower(item.value)]).flag
        return _.set(item, 'flag', flag)
      })
    },

    knowledgeModelImageStyles () {
      const imageUrl = isValidURL(this.newKnowledgeModel.imageSrc)
        ? this.newKnowledgeModel.imageSrc
        : this.defaultImage

      return { 'background-image': `url(${imageUrl})` }
    },

    knowledgeModelExtensions () {
      return this.hasEntityExtraction ? ['luis'] : []
    },

    modelCulture () {
      const culture = this.hasEntityExtraction
        ? this.newKnowledgeModel.culture
        : 'en-US'

      return _.toLower(culture)
    }

  },

  validations: {
    newKnowledgeModel: {
      name: {
        required,
        unique: function (modelName) {
          return !this.knowledgeBases.some(kb => kb.name.toLowerCase() === modelName.toLowerCase())
        }
      },
      imageSrc: {
        validUrl: function (imageURL) {
          return imageURL ? isValidURL(imageURL) : true
        }
      }
    }
  },

  methods: {
    ...mapActions('knowledgebase', [
      'createKnowledgeBase',
      'importModel',
      'initNluApplication'
    ]),
    ...mapActions('knowledgebase/files', [
      'uploadFile',
      'getDownloadUrl'
    ]),

    openModal () {
      this.$refs['new-model-modal'].open()
      const limit = get(this.limits, 'KNOWLEDGE_BASES.ENTITY_EXTRACTION_KM_NUMBER', 10)
      this.extractionKmNumber = limit
      this.warningLabel = this.$t('common.modals.create_modal.youCanCreate', { limit })
    },

    closeModal () {
      this.$refs['new-model-modal'].close()
      this.resetForm()
    },

    async onSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) return

      this.isLoading = true
      try {
        if (_.isEmpty(this.files)) {
          await this.createKnowledgeBase({
            name: this.newKnowledgeModel.name,
            desc: this.newKnowledgeModel.desc,
            imageSrc: this.newKnowledgeModel.imageSrc || this.defaultImage,
            extensions: this.knowledgeModelExtensions,
            culture: this.modelCulture
          })
        } else {
          await this.importKnowledgeModel()
        }
        this.closeModal()
        await this.initNluApplication()
      } catch (err) {
        this.creationError = err.isAxiosError
          ? err.response.data.message || err.message
          : err.message
        console.error(`Wasn't able to create knowledge model: ${err.toString()}`)
      }
      this.isLoading = false
    },

    async importKnowledgeModel () {
      const [file] = this.files
      const prefix = `${this.userId}/${Date.now()}_`
      const fileModel = { name: file.name, type: file.type, file }
      const key = `${prefix}${file.name}`
      const data = { prefix, fileModel, isPublic: true }
      await this.uploadFile(data)
      const downloadUrl = await this.getDownloadUrl({ key: key, isPublic: true })

      let modelType
      switch (file.type) {
        case this.supportedFileTypes.JSON: {
          modelType = 'json'
          break
        }
        // Note: 'csv' file has a lot of possible variants and can be tricky to detect
        // https://stackoverflow.com/questions/34390747/how-can-i-validate-for-only-csv-file-uploads-using-the-pattern-attribute-using-h/34390878
        case this.supportedFileTypes.CSV: {
          modelType = 'csv'
          break
        }
        default: {
          const nameParts = file.name.split('.')
          if (nameParts[nameParts.length - 1] === 'csv') {
            modelType = 'csv'
          } else {
            modelType = 'json'
          }
        }
      }

      await this.importModel({
        fileUrl: downloadUrl,
        type: modelType,
        knowledgeBase: {
          ...this.newKnowledgeModel,
          extensions: this.knowledgeModelExtensions
        }
      })
    },

    onFileSelection (evt) {
      const files = evt.srcElement.files
      const importFile = files[0]
      this.files.push(importFile)
      this.isImportMode = false
    },

    deleteFile () {
      this.files = []
      this.creationError = null
    },

    resetForm () {
      Object.assign(this.$data, this.$options.data.apply(this))
      this.creationError = null
      this.$v.$reset()
    }
  },

  watch: {
    limits: {
      handler (value) {
        this.extractionKmNumber = get(value, 'KNOWLEDGE_BASES.ENTITY_EXTRACTION_KM_NUMBER', 10)
      },
      deep: true
    },
    extractionKmNumber (value) {
      this.warningLabel = this.$t('common.modals.create_modal.youCanCreate', { limit: value })
    }
  }
}
</script>

<style lang="scss" scoped>
.wrap {
    padding-bottom: $sp-3;

    .import-wrap {
        &__wrapper{
            height: 350px;
        }

        .btn-group {
            display: flex;
            justify-content: flex-end;
            flex-grow: 1;
        }

        .drag-zone {
            display: flex;
            position: relative;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: calc(100% - 16px);
            border: 1px dashed $or-grey-medium;
            border-radius: 4px;
            margin-bottom: $sp-2;

            .cloud {
                font-size: 95px;
            }

            .drag-title {
                text-align: center;
                font-size: 14px;
                color: $or-grey-dark;
                cursor: pointer;

                .bold {
                    font-weight: bold;
                    color: $or-blue;
                }
            }

            .drag-input {
                position: absolute;
                width: 100%;
                height: 100%;
                top: 0;
                left: 0;
                opacity: 0;
            }
        }
        .drag-zone_dragged {
            border: 2px dashed $or-grey-medium;
            background-color: $or-grey-light;
        }
    }

    .entity-wrap {
        margin-top: $sp-3;
    }

    &__img-row {
        display: flex;
        align-items: center;

        .img-wrap {
            width: 100px;
            height: 100px;
            margin-right: $sp-5;
            border-radius: 100%;
            background-repeat: no-repeat;
            background-position: center;
            background-size: cover;
            background-color: $or-grey-medium;
        }
    }

    .lang-option {
        img: {
            width: 20px;
            height: 20px;
        }
    }

    &__btn-row {
        display: flex;
        margin-top: 30px;
        justify-content: space-between;
        align-items: center;

        .file-wrap {
            display: flex;
            align-items: center;

            .file-label {
                color: $or-black-blue;
                font-size: 14px;
                margin-top: $sp-1;
            }

            .delete-btn {
                color: $or-grey;
                font-size: 20px;

                &:hover {
                    color: darken($or-grey, 10%);
                    cursor: pointer;
                }
            }
        }
    }
    .creation-error {
        margin-top: 10px;
        color: red;
    }
}
</style>

<style lang="scss">
.wrap {
    .lang-select {
        .ui-select-option {
            display: flex;
            height: 32px;
            align-items: center;

            img {
                width: 25px;
            }
        }
    }

    .ui-textbox {
        margin-bottom: 0;

        &.kb-title {
            margin-bottom: 4px;
        }
    }

    .ui-modal {
        &__container {
            max-width: 480px;
            border-radius: 4px;
        }

        &__header-text {
            color: $or-black-blue;
            font-weight: bold;
        }
    }

    .ui-modal__wrapper > .ui-modal__container > .ui-modal__header h1 {
        font-size: 18px;
    }

    .img-input {
        .ui-textbox__input {
            min-height: 30px;
            width: 230px;
        }
    }

    button.ui-button.ui-button--type-primary.ui-button--color-primary {
        background-color: $or-blue;

        &:hover {
            background: darken($or-blue, 10%);
        }

        &.is-disabled:hover {
            background: $or-blue;
        }
    }
}
</style>
